
import React from 'react';
import { PCDBPart, VMRSPart } from '@/types/parts';
import { Badge } from '@/components/ui/badge';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import '../styles/UserPartsDisplay.css';

interface UserPartsGridProps {
  pcdbParts?: PCDBPart[];
  vmrsParts?: VMRSPart[];
  compact?: boolean;
}

const UserPartsGrid: React.FC<UserPartsGridProps> = ({ 
  pcdbParts = [], 
  vmrsParts = [], 
  compact = false 
}) => {
  const handleCopyPart = React.useCallback((partText: string) => {
    navigator.clipboard.writeText(partText);
    toast.success('Part copied to clipboard');
  }, []);

  const renderPCDBPart = React.useCallback((part: PCDBPart, index: number) => (
    <div key={`pcdb-${index}`} className={`user-part-card pcdb ${compact ? 'compact' : ''}`}>
      <div className="part-content">
        <div className="part-title">{part.part_terminology_name}</div>
        <div className="part-meta">
          <Badge variant="outline">{part.category}</Badge>
          {part.sub_category && (
            <Badge variant="secondary">{part.sub_category}</Badge>
          )}
        </div>
      </div>
      <button
        className="part-copy-btn"
        onClick={() => handleCopyPart(`${part.part_terminology_name} (${part.category})`)}
        aria-label="Copy part"
      >
        <Copy size={12} />
      </button>
    </div>
  ), [compact, handleCopyPart]);

  const renderVMRSPart = React.useCallback((part: VMRSPart, index: number) => (
    <div key={`vmrs-${index}`} className={`user-part-card vmrs ${compact ? 'compact' : ''}`}>
      <div className="part-content">
        <div className="part-title">{part.part_description}</div>
        <div className="part-meta">
          <Badge variant="outline">{part.vmrs_code}</Badge>
        </div>
      </div>
      <button
        className="part-copy-btn"
        onClick={() => handleCopyPart(`${part.part_description} (${part.vmrs_code})`)}
        aria-label="Copy part"
      >
        <Copy size={12} />
      </button>
    </div>
  ), [compact, handleCopyPart]);

  const totalParts = pcdbParts.length + vmrsParts.length;

  if (totalParts === 0) {
    return null;
  }

  return (
    <div className="user-parts-grid">
      <div className="parts-header">
        <span className="parts-count">{totalParts} part{totalParts !== 1 ? 's' : ''} found</span>
      </div>
      
      <div className="parts-container">
        {pcdbParts.length > 0 && (
          <div className="parts-section">
            <div className="section-label">PCDB Parts</div>
            <div className="parts-list">
              {pcdbParts.map(renderPCDBPart)}
            </div>
          </div>
        )}
        
        {vmrsParts.length > 0 && (
          <div className="parts-section">
            <div className="section-label">VMRS Parts</div>
            <div className="parts-list">
              {vmrsParts.map(renderVMRSPart)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPartsGrid;
